import React, { useState, useEffect } from "react";
import { XIcon, CameraIcon, ImageIcon, UploadIcon } from "./icons/FeatherIcons";
import { Capacitor } from "@capacitor/core";
import { CameraService, PhotoOptions } from "../services/cameraService";
import { usePageTracking } from "@/hooks/usePageTracking";

interface EnhancedPhotoSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (dataUrl: string) => void;
  title?: string;
  allowCamera?: boolean;
  allowGallery?: boolean;
  photoOptions?: PhotoOptions;
}

const EnhancedPhotoSelectionModal: React.FC<
  EnhancedPhotoSelectionModalProps
> = ({
  isOpen,
  onClose,
  onSelect,
  title = "Select Photo",
  allowCamera = true,
  allowGallery = true,
  photoOptions = {
    quality: 90,
    width: 1024,
    height: 1024,
  },
}) => {
  usePageTracking("EnhancedPhotoSelection", "EnhancedPhotoSelectionModal");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [previewImage, setPreviewImage] = useState<string>("");
  const [hasPermissions, setHasPermissions] = useState(false);

  const isNativePlatform = Capacitor.isNativePlatform();

  // Check permissions on modal open
  useEffect(() => {
    if (isOpen && isNativePlatform) {
      checkPermissions();
    }
  }, [isOpen, isNativePlatform]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setPreviewImage("");
      setError("");
      setIsLoading(false);
    }
  }, [isOpen]);

  const checkPermissions = async () => {
    try {
      const granted = await CameraService.checkCameraAndPhotosPermissions();
      setHasPermissions(granted);

      if (!granted) {
        const requested =
          await CameraService.requestCameraAndPhotosPermissions();
        setHasPermissions(requested);
      }
    } catch (error) {
      setHasPermissions(false);
    }
  };

  const handleCameraPhoto = async () => {
    setIsLoading(true);
    setError("");

    try {
      if (isNativePlatform && !hasPermissions) {
        const granted = await CameraService.checkAndRequestCameraPermissions();
        if (!granted) {
          setError("Camera permission is required to take photos");
          setIsLoading(false);
          return;
        }
        setHasPermissions(true);
      }

      const result = await CameraService.takePhoto({
        quality: photoOptions?.quality || 90,
        width: photoOptions?.width || 1024,
        height: photoOptions?.height || 1024,
      });

      if (result.success && result.imageUrl) {
        setPreviewImage(result.imageUrl);
        onSelect(result.imageUrl);
        onClose();
      } else {
        setError(result.error || "Failed to take photo");
      }
    } catch (error) {
      setError("Camera access failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGalleryPhoto = async () => {
    setIsLoading(true);
    setError("");

    try {
      if (isNativePlatform) {
        if (!hasPermissions) {
          const granted =
            await CameraService.checkAndRequestPhotosPermissions();
          if (!granted) {
            setError("Photo library permission is required");
            setIsLoading(false);
            return;
          }
          setHasPermissions(true);
        }

        const result = await CameraService.selectFromGallery({
          quality: photoOptions?.quality || 90,
          width: photoOptions?.width || 1024,
          height: photoOptions?.height || 1024,
        });

        if (result.success && result.imageUrl) {
          setPreviewImage(result.imageUrl);
          onSelect(result.imageUrl);
          onClose();
        } else {
          setError(result.error || "Failed to select photo");
        }
      } else {
        // Web platform - use file input
        const result = await CameraService.selectPhotoWithOptions({
          quality: photoOptions?.quality || 90,
          width: photoOptions?.width || 1024,
          height: photoOptions?.height || 1024,
        });

        if (result.success && result.imageUrl) {
          setPreviewImage(result.imageUrl);
          onSelect(result.imageUrl);
          onClose();
        } else {
          setError(result.error || "Failed to select photo");
        }
      }
    } catch (error) {
      setError("Failed to access photos. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[120] p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-sm overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <XIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-3">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Camera Option */}
          {allowCamera && (
            <button
              onClick={handleCameraPhoto}
              disabled={isLoading}
              className="w-full flex items-center p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                <CameraIcon className="w-6 h-6 text-white" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-gray-900">
                  {isNativePlatform ? "Take Photo" : "Camera"}
                </h3>
                <p className="text-sm text-gray-600">
                  {isNativePlatform
                    ? "Use camera to take a new photo"
                    : "Use camera to capture"}
                </p>
              </div>
              {isLoading && (
                <div className="ml-auto">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                </div>
              )}
            </button>
          )}

          {/* Gallery/Upload Option */}
          {allowGallery && (
            <button
              onClick={handleGalleryPhoto}
              disabled={isLoading}
              className="w-full flex items-center p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                {isNativePlatform ? (
                  <ImageIcon className="w-6 h-6 text-white" />
                ) : (
                  <UploadIcon className="w-6 h-6 text-white" />
                )}
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-gray-900">
                  {isNativePlatform ? "Choose from Gallery" : "Upload Photo"}
                </h3>
                <p className="text-sm text-gray-600">
                  {isNativePlatform
                    ? "Select from your photo library"
                    : "Choose a photo from your device"}
                </p>
              </div>
              {isLoading && (
                <div className="ml-auto">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-500"></div>
                </div>
              )}
            </button>
          )}

          {/* Permissions Notice for Web */}
          {!isNativePlatform && allowCamera && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-blue-700 text-xs">
                💡 <strong>Tip:</strong> Install this app on your mobile device
                for native camera access and better photo features.
              </p>
            </div>
          )}

          {/* Format Support Info */}
          <div className="text-center pt-2">
            <p className="text-xs text-gray-500">
              Supports JPEG, PNG, WebP • Max 10MB
            </p>
          </div>
        </div>

        {/* Cancel Button */}
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="w-full py-3 text-gray-600 font-medium hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default EnhancedPhotoSelectionModal;
